import { useMemo, useState } from "react";
import {
  DEFAULT_API_BASE,
  useCancelJobRun,
  useClearFinishedJobRuns,
  useDeleteJobRun,
  useJobRuns,
} from "./api";
import type { JobRun } from "./types";

export interface JobsPageProps {
  /** Override the default `/api/jobs` mount point. Trailing slash NOT included. */
  apiBase?: string;
  /** Per-job display label map. Unknown keys fall through to the raw job name. */
  labels?: Record<string, string>;
  /** Custom renderer for the rightmost "Details" column. Falls back to a
   *  generic message/details_json view. */
  renderDetails?: (run: JobRun, details: unknown) => React.ReactNode;
  /** Polling cadence in ms. Default 5000. */
  refetchMs?: number;
  /** Show row-level Cancel + Delete and the global "Clear finished" action.
   *  Defaults to true; set false for read-only viewers. */
  showActions?: boolean;
}

/**
 * Generic admin Jobs page powered by saas-common/jobruns. Drop into any
 * service that mounts `jobruns.HTTPHandlers` at `apiBase` (default `/api/jobs`).
 *
 * Auto-refreshes every 5s. Filter chips derive from the summary endpoint —
 * any job that has ever recorded a run shows up automatically.
 */
export function JobsPage({
  apiBase = DEFAULT_API_BASE,
  labels = {},
  renderDetails,
  refetchMs,
  showActions = true,
}: JobsPageProps) {
  const [jobFilter, setJobFilter] = useState<string>("");
  const { data, isLoading } = useJobRuns({
    apiBase,
    job: jobFilter || undefined,
    refetchMs,
  });

  const cancelMut = useCancelJobRun(apiBase);
  const deleteMut = useDeleteJobRun(apiBase);
  const clearMut = useClearFinishedJobRuns(apiBase);

  const runs = data?.runs ?? [];
  const summary = data?.summary ?? [];
  const jobs = useMemo(
    () => ["", ...summary.map((s) => s.job)],
    [summary],
  );
  const jobLabel = (key: string) => labels[key] ?? key;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Jobs</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Background and scheduled job execution history. Auto-refreshes every {Math.round((refetchMs ?? 5000) / 1000)}s.
          </p>
        </div>
        {showActions && (
          <button
            onClick={() => {
              if (window.confirm("Delete all finished/cancelled rows?")) clearMut.mutate();
            }}
            disabled={clearMut.isPending}
            className="whitespace-nowrap text-xs px-3 py-1.5 rounded-md border border-border bg-card text-muted-foreground hover:bg-muted/60 transition disabled:opacity-50"
          >
            {clearMut.isPending ? "Clearing…" : "Clear finished"}
          </button>
        )}
      </header>

      {/* Per-job summary cards */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {summary.length === 0 ? (
          <div className="rounded-xl border border-border bg-card shadow-sm p-6 text-sm text-muted-foreground">
            No job runs recorded yet.
          </div>
        ) : (
          summary.map((s) => (
            <button
              key={s.job}
              onClick={() => setJobFilter(jobFilter === s.job ? "" : s.job)}
              className={
                "text-left rounded-xl border shadow-sm p-4 bg-card hover:shadow-md transition " +
                (jobFilter === s.job
                  ? "border-primary ring-2 ring-primary/30"
                  : "border-border")
              }
            >
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">{jobLabel(s.job)}</div>
                <StatusPill status={s.last_status} />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <Metric label="24h OK" value={s.ok_24h} tone="emerald" />
                <Metric label="24h Err" value={s.error_24h} tone="rose" />
                <Metric
                  label="Running"
                  value={s.running}
                  tone={s.running > 0 ? "blue" : "muted"}
                />
              </div>
              <div className="mt-3 text-[11px] text-muted-foreground">
                {s.last_run_at
                  ? `Last: ${new Date(s.last_run_at).toLocaleString()}`
                  : "Never run"}
              </div>
            </button>
          ))
        )}
      </section>

      {/* Filter chips */}
      {summary.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">Filter:</span>
          {jobs.map((j) => {
            const active = jobFilter === j;
            const label = j ? jobLabel(j) : "All jobs";
            return (
              <button
                key={j || "all"}
                onClick={() => setJobFilter(j)}
                className={
                  "px-2.5 py-1 rounded-full border transition " +
                  (active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card text-muted-foreground hover:bg-muted/60")
                }
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* Runs table */}
      <section className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Execution history
          </h2>
          <span className="text-xs text-muted-foreground">
            {runs.length} runs {jobFilter && `· filter: ${jobLabel(jobFilter)}`}
          </span>
        </div>
        {isLoading ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            Loading…
          </div>
        ) : runs.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            No runs yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground bg-muted/40">
                <tr>
                  <th className="py-2.5 px-5">Job</th>
                  <th className="py-2.5">Status</th>
                  <th className="py-2.5">Trigger</th>
                  <th className="py-2.5">Started</th>
                  <th className="py-2.5">Duration</th>
                  <th className="py-2.5 pr-5">Details</th>
                  {showActions && <th className="py-2.5 pr-5"></th>}
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <RunRow
                    key={r.id}
                    run={r}
                    label={jobLabel(r.job)}
                    renderDetails={renderDetails}
                    showActions={showActions}
                    onCancel={() => cancelMut.mutate(r.id)}
                    onDelete={() => {
                      if (window.confirm(`Delete run #${r.id}?`)) deleteMut.mutate(r.id);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function RunRow({
  run,
  label,
  renderDetails,
  showActions,
  onCancel,
  onDelete,
}: {
  run: JobRun;
  label: string;
  renderDetails?: (run: JobRun, details: unknown) => React.ReactNode;
  showActions: boolean;
  onCancel: () => void;
  onDelete: () => void;
}) {
  const details = useMemo(() => safeParse(run.details_json), [run.details_json]);
  return (
    <tr className="border-t border-border hover:bg-muted/30 transition">
      <td className="py-3 px-5 font-medium">{label}</td>
      <td>
        <StatusPill status={run.status} />
      </td>
      <td>
        <span
          className={
            "text-[10px] px-2 py-0.5 rounded-md font-medium " +
            (run.trigger === "manual"
              ? "bg-primary/10 text-primary"
              : "bg-muted text-muted-foreground")
          }
        >
          {run.trigger}
        </span>
      </td>
      <td className="text-muted-foreground text-xs tabular-nums">
        {new Date(run.started_at).toLocaleString()}
      </td>
      <td className="tabular-nums text-xs">
        {run.status === "running" ? (
          <span className="text-blue-600 dark:text-blue-400">in progress…</span>
        ) : run.duration_ms > 0 ? (
          formatDuration(run.duration_ms)
        ) : (
          "—"
        )}
      </td>
      <td className="pr-5 text-xs text-muted-foreground">
        {renderDetails ? renderDetails(run, details) : <DefaultDetail run={run} />}
      </td>
      {showActions && (
        <td className="pr-5 text-right whitespace-nowrap">
          {run.status === "running" ? (
            <button
              onClick={onCancel}
              className="text-[10px] px-2 py-0.5 rounded-md border border-border text-muted-foreground hover:bg-muted/60"
            >
              Cancel
            </button>
          ) : (
            <button
              onClick={onDelete}
              className="text-[10px] px-2 py-0.5 rounded-md border border-border text-muted-foreground hover:bg-destructive/10 hover:text-destructive hover:border-destructive/40"
            >
              Delete
            </button>
          )}
        </td>
      )}
    </tr>
  );
}

function DefaultDetail({ run }: { run: JobRun }) {
  if (run.status === "error" || run.status === "cancelled") {
    return <span className="text-destructive">{run.message || run.status}</span>;
  }
  return <>{run.message || "—"}</>;
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    ok: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
    error: "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300",
    running: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
    skipped: "bg-muted text-muted-foreground",
    cancelled: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  };
  const icon: Record<string, string> = {
    ok: "✓",
    error: "✗",
    running: "●",
    skipped: "–",
    cancelled: "⊘",
  };
  return (
    <span
      className={
        "inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md " +
        (map[status] ?? map.skipped)
      }
    >
      <span className={status === "running" ? "animate-pulse" : ""}>
        {icon[status] ?? "·"}
      </span>
      {status}
    </span>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "emerald" | "rose" | "blue" | "muted";
}) {
  const cls: Record<string, string> = {
    emerald: "text-emerald-600 dark:text-emerald-400",
    rose: "text-rose-600 dark:text-rose-400",
    blue: "text-blue-600 dark:text-blue-400",
    muted: "text-muted-foreground",
  };
  return (
    <div className="rounded-md bg-muted/40 py-1.5">
      <div className={`text-lg font-bold tabular-nums ${cls[tone]}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}m ${r}s`;
}

function safeParse(s: string): unknown {
  if (!s) return null;
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}
